import {StandardDialogFooter} from "../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import React, {useEffect, useState} from "react";
import {DialogContent, Grid} from "@mui/material";
import {OrderModel} from "../../../../pages/shared/models/order/order-item.model";
import {OrderGroupModel} from "../../../../pages/shared/models/order/orderGroupModel";
import {BarekamSelect} from "../../../../pages/components/select/select";
import {BarekamMenuItem, SelectedBarekamMenuItem} from "../../../../pages/components/select/styled/orders";
import BarekamInput from "../../../../pages/components/input";
import {useAppDispatch, useAppSelector} from "../../../../redux/store";
import {Message, MessageSections} from "../../../../pages/components/message/message";
import {WarehouseModel} from "../../../../pages/shared/models/order/warehouse.model";
import orderGroupService from "../../../../services/order-group.service";
import {AxiosError} from "axios";
import {mapValidationErrors} from "../../../../pages/user/settings/personal-info/personal-info.service";
import {getOrderGroups} from "../../../../redux/slices/order-group.slice";
import {getMessage} from "../../../../pages/shared/helper-functions";

const NewGroup = ({group, onCancel}: NewGroupDialogProps) => {
    const [groupData, setGroupData] = useState<OrderGroupModel>(group || {
        destination: undefined,
        orders: [] as OrderModel[],
        driverFullName: '',
        driverPhoneNumber: '',
        driverCarInfo: '',
        note: '',
    })

    const [warehouses, setWarehouses] = useState<WarehouseModel[]>([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const {orderStateId} = useAppSelector(state => state.ordersReducer);
    const {countries} = useAppSelector(state => state.staticDataReducer);
    const {messageCache, languageId} = useAppSelector(state => state.messagesReducer);

    const dispatch = useAppDispatch();

    useEffect(() => {
        setWarehouses(() => {
            return countries
                .map(country => country.warehouse)
                .flat();
        })
    }, [countries])

    function handleChange(field: 'destination' | 'driverFullName' | 'driverPhoneNumber' | 'driverCarInfo' | 'note', value: any) {
        setGroupData(prevState => {
            const stateClone = {...prevState} as OrderGroupModel;

            switch (field) {
                case "destination":
                    stateClone.destination = warehouses.find(warehouse => warehouse.warehouseId === value);
                    break;
                default:
                    stateClone[field] = value;
                    break;
            }

            return stateClone;
        })
    }

    function validateGroup() {
        const errors = {} as {[key: string]: string};

        if (!groupData.destination) {
            errors['destination'] = 'required';
        }

        return errors;
    }

    function createGroup() {
        const errors = validateGroup();
        setErrors(errors);

        if (Object.keys(errors).length === 0 && groupData) {
            orderGroupService.createGroup(groupData)
                .then((response: any) => {
                    if (response) {
                        dispatch(getOrderGroups({forceLoad: true, page: 0, orderStateId: orderStateId as number}));
                        onCancel();
                    }
                })
                .catch(e => {
                    if (e instanceof AxiosError) {
                        setErrors(mapValidationErrors(e));
                    }
                });
        }
    }

    return (
        <>
            <DialogContent>
                <Grid container rowSpacing={'1rem'} columnSpacing={'1rem'}>
                    <Grid item xs={12}>
                        <BarekamSelect
                            variant={'outlined'}
                            label={getMessage('destination', MessageSections.ORDERS, messageCache, languageId)}
                            value={groupData.destination?.warehouseId || ''}
                            onChange={(event) => handleChange('destination', event.target.value as number)}
                            type={"standard"}
                            error={!!errors['destination']}
                            errorMessage={errors['destination'] && getMessage(errors['destination'], MessageSections.ORDERS, messageCache, languageId)}>
                            {warehouses.map((warehouse, index) => {
                                if (groupData.destination?.warehouseId === warehouse.warehouseId) {
                                    return (
                                        <SelectedBarekamMenuItem value={warehouse.warehouseId}
                                                                 key={index}>
                                            <Message messageKey={warehouse.city} sectionId={MessageSections.MAIN}/>
                                        </SelectedBarekamMenuItem>
                                    )
                                } else {
                                    return (
                                        <BarekamMenuItem value={warehouse.warehouseId}
                                                         key={index}>
                                            <Message messageKey={warehouse.city} sectionId={MessageSections.MAIN}/>
                                        </BarekamMenuItem>
                                    );
                                }
                            })}
                        </BarekamSelect>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamInput value={groupData.note}
                                      placeholder={getMessage('note', MessageSections.ORDERS, messageCache, languageId)}
                                      label={getMessage('note', MessageSections.ORDERS, messageCache, languageId)}
                                      customInputVariant={"standard"}
                                      width={'100%'}
                                      onChange={(event) => handleChange('note', event.target.value)}
                                      error={!!errors['note']}
                                      errorMessage={errors['note'] && getMessage(errors['destination'], MessageSections.ORDERS, messageCache, languageId)}/>
                    </Grid>
                </Grid>
            </DialogContent>

            <StandardDialogFooter onCancel={() => onCancel()}
                                  onSave={() => createGroup()}
                                  saveLabel={<Message messageKey={'add_group'} sectionId={MessageSections.ORDERS}/>}/>
        </>
    );
}

interface NewGroupDialogProps {
    group?: OrderGroupModel,
    onCancel: () => void,
}

export default NewGroup;